/**
 * Classification des échecs d'appel API, partagée par TOUS les patterns.
 *
 * Règle 2 du CLAUDE.md racine : une fonction coupée répond **501 `feature_disabled`**, jamais 404.
 * Ce n'est donc pas un plantage mais une réponse légitime du contrat : les patterns l'affichent
 * comme « fonction indisponible », sans toast rouge et sans bouton « Réessayer ».
 */
import { ApiError } from '@graal/api';
import type { TFunction } from 'i18next';

export type ProblemKind = 'forbidden' | 'not-found' | 'unavailable' | 'busy' | 'error';

/**
 * `unavailable` = 501 (fonction coupée) · `busy` = 503/504 (base momentanément occupée ou lente :
 * réessayer a un sens, l'inverse du 501) · `forbidden` = 401/403 · `not-found` = 404.
 */
export function classifyError(error: unknown): ProblemKind {
  if (error instanceof ApiError) {
    if (error.status === 501) return 'unavailable';
    if (error.isBusy) return 'busy';
    if (error.status === 401 || error.status === 403) return 'forbidden';
    if (error.status === 404) return 'not-found';
  }
  return 'error';
}

/** Vrai seulement pour le 501 `feature_disabled` du contrat (pas pour un 501 nu). */
export function isFeatureDisabled(error: unknown): boolean {
  return error instanceof ApiError && error.isFeatureDisabled;
}

type ContractError = { code?: unknown; message?: unknown; reason?: unknown; hint?: unknown };

/**
 * Détail lisible d'une erreur du contrat (`common-v100.yaml#/components/schemas/Error` :
 * `code`, `message`, `reason`, `hint`). On préfère `reason` (la cause métier) puis `message`.
 * Jamais affiché seul : c'est un complément sous un titre traduit.
 */
export function errorDetail(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const body = (typeof error.body === 'object' && error.body !== null ? error.body : {}) as ContractError;
  const candidates = [body.reason, body.message, body.hint];
  const detail = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  return typeof detail === 'string' ? detail : undefined;
}

/** Code machine de l'erreur du contrat (`feature_disabled`, `project_not_found`…). */
export function errorCode(error: unknown): string | undefined {
  return error instanceof ApiError ? error.code : undefined;
}

/**
 * Référence de corrélation d'un échec SERVEUR (5xx, hors 501 `feature_disabled` qui n'est pas une
 * panne) : le `traceId` de l'`ApiError` (corps `Error.traceId`, sinon en-tête `X-Request-Id`).
 * C'est ce que l'utilisateur recopie au support pour retrouver la requête dans les journaux.
 * `undefined` sur un 4xx (la cause est dans le message) ou tant que l'API ne la pose pas.
 */
export function errorReference(error: unknown): string | undefined {
  if (!(error instanceof ApiError) || error.status < 500 || error.status === 501) return undefined;
  return error.traceId;
}

/** Forme affichée d'une référence : ses 8 premiers caractères (la valeur copiée reste entière). */
export const shortReference = (traceId: string): string => traceId.slice(0, 8);

/** Le `Retry-After` d'un 503/504, en secondes, s'il est connu. */
export function retryAfterSeconds(error: unknown): number | undefined {
  return error instanceof ApiError ? error.retryAfterSeconds : undefined;
}

/**
 * Description traduite d'un échec, pour les bannières des formulaires et des assistants. Refus de
 * droit et base occupée : le texte traduit SEUL (le `message` du contrat est un texte anglais
 * technique) ; sinon la raison du contrat, à défaut le texte générique.
 */
/**
 * Codes d'erreur du contrat qui ont un texte TRADUIT (`patterns.codes.<CODE>`) : le `message` du
 * contrat est un texte anglais technique, la raison réelle d'un refus mérite mieux. Ajouter un code
 * ici, c'est s'engager à le traduire en fr ET en en.
 */
export const TRANSLATED_CODES = [
  'SECRET_MANAGED',
  'SECRET_INVALID',
  // 078272fc : cluster d'un locataire (infrastructure non fiable) — job à secret, charge relayée.
  'INFRASTRUCTURE_NOT_TRUSTED',
  'INFRASTRUCTURE_WORKLOAD_UNSUPPORTED',
  // ADR 0095 : le coffre externe du locataire, sur les écrans des secrets (créer, remplacer une valeur).
  'SECRET_STORE_UNAVAILABLE',
  'SECRET_STORE_DENIED',
  'SECRET_STORE_VALUE_MISSING',
  'SECRET_STORE_NOT_CONFIGURED',
] as const;

export function problemDescription(t: TFunction, kind: ProblemKind, error: unknown): string {
  if (kind === 'busy') {
    const seconds = retryAfterSeconds(error);
    return seconds === undefined
      ? t('patterns.states.busy.description')
      : t('patterns.states.busy.retryIn', { count: seconds });
  }
  const code = TRANSLATED_CODES.find((known) => known === errorCode(error));
  if (code) return t(`patterns.codes.${code}`);
  return (kind === 'forbidden' ? undefined : errorDetail(error)) ?? t(`patterns.states.${kind}.description`);
}
